import Link from "next/link";
import type { MouseEventHandler, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

export type NavigationItemProps = {
  href: string;
  label: string;
  Icon: LucideIcon;
  active?: boolean;
  compact?: boolean;
  mobile?: boolean;
  indicator?: boolean;
  badge?: ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};

export function NavigationItem({
  href,
  label,
  Icon,
  active = false,
  compact = false,
  mobile = false,
  indicator = false,
  badge,
  onClick,
}: NavigationItemProps) {
  if (mobile) {
    return (
      <Link
        href={href}
        prefetch
        onClick={onClick}
        aria-current={active ? "page" : undefined}
        aria-label={label}
        className="flex flex-1 flex-col items-center gap-0.5 py-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--fz-sidebar-active-icon)]"
      >
        <span
          className={`relative flex h-7 w-10 items-center justify-center rounded-xl transition-colors ${
            active ? "bg-[var(--fz-sidebar-active-bg)]" : ""
          }`}
        >
          <Icon className={`h-5 w-5 transition-colors ${active ? "text-[var(--fz-sidebar-active-icon)]" : "text-[var(--fz-sidebar-icon)]"}`} />
          {indicator ? <span className="absolute right-1 top-0 h-2.5 w-2.5 rounded-full border-2 border-[var(--fz-sidebar-bg)] bg-[var(--fz-sidebar-badge-bg)]" /> : null}
          {badge ? <span className="absolute right-0 top-0">{badge}</span> : null}
        </span>
        <span className={`text-[10px] font-medium transition-colors ${active ? "text-[var(--fz-sidebar-active-text)]" : "text-[var(--fz-sidebar-text)]"}`}>
          {label}
        </span>
      </Link>
    );
  }

  return (
    <Link
      href={href}
      prefetch
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      aria-label={compact ? label : undefined}
      title={compact ? label : undefined}
      className={`group flex items-center rounded-[12px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[var(--fz-sidebar-active-icon)] ${
        active
          ? "bg-[var(--fz-sidebar-active-bg)] text-[var(--fz-sidebar-active-text)]"
          : "text-[var(--fz-sidebar-text)] hover:bg-[var(--fz-sidebar-hover-bg)]"
      } ${compact ? "h-10 w-full justify-center px-0 text-[14px]" : "h-9 w-full gap-2 px-2.5 text-[12.5px]"}`}
    >
      <span className={`relative flex flex-shrink-0 items-center justify-center rounded-[9px] bg-[var(--fz-sidebar-hover-bg)] ${compact ? "h-8 w-8" : "h-7 w-7"}`}>
        <Icon className={`${compact ? "h-4.5 w-4.5" : "h-4 w-4"} ${active ? "text-[var(--fz-sidebar-active-icon)]" : "text-[var(--fz-sidebar-icon)]"}`} />
        {indicator ? <span className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full border-2 border-[var(--fz-sidebar-bg)] bg-[var(--fz-sidebar-badge-bg)]" /> : null}
        {badge ? <span className="absolute right-0 top-0">{badge}</span> : null}
      </span>
      {!compact ? <span className="min-w-0 truncate">{label}</span> : null}
    </Link>
  );
}
